import { Search, Utensils, Tag, AlertTriangle, ScanBarcode } from "lucide-react";
import { formatCurrency, resolveMediaUrl } from "../../lib/utils";
import { promotionFor, promoLabel } from "../../lib/promotions";

const ProductCard = ({ product, onAdd, promos, parentOf }) => {
  const out = product.track_stock && (product.current_stock ?? 0) <= 0;
  const low = product.track_stock && !out && (product.current_stock ?? 0) <= (product.min_stock ?? 0);
  const { price, promo, original } = promotionFor(product, promos, parentOf);
  return (
    <button
      onClick={() => !out && onAdd(product)}
      disabled={out}
      className={`group text-left rounded-lg bg-[#262626] border overflow-hidden hover:border-emerald-500/60 hover:-translate-y-0.5 transition-[transform,border-color] active:scale-[0.98] disabled:opacity-40 disabled:hover:translate-y-0 ${low ? "border-amber-500/60" : "border-white/5"}`}
      data-testid={`product-${product.product_id}`}
    >
      <div className="aspect-[4/3] bg-[#303030] relative overflow-hidden">
        {product.image_url ? (
          <img src={resolveMediaUrl(product.image_url)} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#3a3a3a] to-[#222]">
            <Utensils className="w-8 h-8 text-zinc-600" />
          </div>
        )}
        {product.is_popular && !promo && (
          <span className="absolute top-1 left-1 text-[9px] font-bold bg-amber-500 text-black px-1.5 py-0.5 rounded">Popular</span>
        )}
        {promo && (
          <span className="absolute top-1 left-1 text-[9px] font-black bg-red-600 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5 shadow" data-testid={`promo-badge-${product.product_id}`} title={promo.name}>
            <Tag className="w-2.5 h-2.5" /> {promoLabel(promo)}
          </span>
        )}
        {out && (
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-red-300 bg-black/60">ESGOTADO</span>
        )}
        {product.track_stock && !out && (
          <span className={`absolute bottom-1 right-1 text-[9px] px-1 rounded ${low ? "bg-amber-500 text-black font-bold" : "bg-black/60 text-zinc-200"}`} data-testid={low ? `low-stock-badge-${product.product_id}` : undefined}>
            {low && <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />}
            {low ? `STOCK BAIXO (${product.current_stock})` : `${product.current_stock} ${product.stock_unit || "un."}`}
          </span>
        )}
      </div>
      <div className="p-2">
        <p className="text-[11px] font-semibold text-zinc-100 leading-tight line-clamp-2">{product.name}</p>
        {product.description && <p className="text-[10px] text-zinc-500 truncate">{product.description}</p>}
        {promo ? (
          <div className="mt-0.5 flex items-baseline gap-1.5 flex-wrap">
            <span className="text-xs font-mono font-bold text-red-400 whitespace-nowrap" data-testid={`promo-price-${product.product_id}`}>{formatCurrency(price)}</span>
            <span className="text-[10px] font-mono text-zinc-500 line-through whitespace-nowrap">{formatCurrency(original)}</span>
          </div>
        ) : (
          <p className="text-xs font-mono font-bold text-emerald-400 mt-0.5 whitespace-nowrap">{formatCurrency(product.price)}</p>
        )}
      </div>
    </button>
  );
};

export const POSProductGrid = ({ products, onAdd, search, setSearch, onSearchEnter, lastScan, categoryName, ad, adCount, adIndex, promos = [], parentOf = {} }) => (
  <div className="flex-1 min-h-0 flex flex-col">
    <div className="flex items-center gap-3 px-3 py-2 border-b border-white/5">
      <h2 className="text-sm font-semibold text-zinc-200 truncate" data-testid="pos-grid-title">{categoryName}</h2>
      <span className="text-[11px] text-zinc-500 shrink-0">{products.length} artigos</span>
      <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${lastScan?.ok === false ? "border-red-500/40 text-red-300" : "border-emerald-500/30 text-emerald-300/80"}`} title="Leitor de código de barras: aponte e dispare — o artigo entra na conta" data-testid="pos-scanner-indicator">
        <ScanBarcode className="w-3 h-3" /> {lastScan ? (lastScan.ok ? `Lido: ${lastScan.name}` : `Não encontrado: ${lastScan.code}`) : "Leitor activo"}
      </span>
      <div className="relative ml-auto w-64 max-w-full">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && onSearchEnter) { e.preventDefault(); onSearchEnter(search); } }}
          className="w-full h-8 pl-8 pr-2 rounded-md bg-[#262626] border border-white/10 text-xs text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500"
          placeholder="Pesquisar ou ler código de barras…"
          data-scan-target="1"
          data-testid="pos-search-input"
        />
      </div>
    </div>
    {promos.length > 0 && (
      <div className="mx-3 mt-2 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-[11px] text-red-200" data-testid="pos-promos-banner">
        <Tag className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">
          <strong>Promoções ativas:</strong> {promos.map((p) => `${p.name} (${promoLabel(p) === "PROMO" ? `-${formatCurrency(p.discount_value)}` : promoLabel(p)})`).join(" · ")} — aplicadas automaticamente
        </span>
      </div>
    )}
    {ad && (
      <div className="mx-3 mt-2 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="pos-ad-banner">
        {ad.image_url && <img src={ad.image_url} alt={ad.title} className="h-9 w-9 rounded object-cover shrink-0" />}
        <div className="min-w-0">
          <p className="font-semibold text-xs text-amber-200 truncate" data-testid="pos-ad-title">{ad.title}</p>
          {ad.message && <p className="text-[11px] text-zinc-400 truncate">{ad.message}</p>}
        </div>
        {adCount > 1 && (
          <div className="ml-auto flex gap-1 shrink-0">
            {Array.from({ length: adCount }).map((_, i) => (
              <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === adIndex ? "bg-amber-400" : "bg-zinc-600"}`} />
            ))}
          </div>
        )}
      </div>
    )}
    <div className="flex-1 overflow-y-auto p-3" data-testid="pos-products-grid">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5">
        {products.map((p) => (
          <ProductCard key={p.product_id} product={p} onAdd={onAdd} promos={promos} parentOf={parentOf} />
        ))}
      </div>
      {products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
          <Utensils className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">Nenhum artigo encontrado</p>
        </div>
      )}
    </div>
  </div>
);
